/* global Word */
/**
 * Listas NATIVAS da marca no documento (Office.js). O que é decisão — níveis,
 * marcador digitado, tipo da lista vizinha — é puro, em `word/listPlan.ts`.
 *
 * A mecânica do Word que este módulo respeita foi MEDIDA (set/2026, nos
 * templates Proposta Técnica e Outro), e cada ponto dela já causou um defeito:
 *
 *  1. Trocar o ESTILO de um item o tira da lista — no "Outro", ainda o joga na
 *     numeração que o estilo "Parágrafo da Lista" carrega. Era a lista numerada
 *     com número só no 1º item (e itens fora de ordem): os demais recebiam o
 *     estilo DEPOIS de entrar na lista.
 *  2. `startNewList()` falha com GeneralException em parágrafo que já é item.
 *  3. Parágrafo NORMAL que entra numa lista (`startNewList`/`attachToList`)
 *     vira "Parágrafo da Lista" sozinho. É o único caminho que deixa o estilo
 *     certo E a lista nossa — por isso quem entra passa por Normal antes.
 *  4. `detachFromList()` devolve o parágrafo em Normal, alinhado à esquerda no
 *     "Outro" e com 18pt de recuo residual: era o "bullet em Normal, à
 *     esquerda" relatado pelo QA. Aplicar um estilo que não é de lista já tira
 *     o parágrafo da lista sem resíduo; o detach fica de rede, com o recuo
 *     zerado depois.
 *  5. Parágrafo inserido antes/depois de um item HERDA a lista, e a numeração
 *     acompanha (1., 2., 3., 4.).
 *
 * Comandos Office.js são enfileirados e só falham no `sync` — cada passo que
 * depende do anterior (sair da lista antes de começar outra) tem o seu.
 */
import { listKindOfLevelType, normalizeLevels, typedListMarker, wordSearchText, } from './listPlan';
import { HOUSE_LISTS, applyHouseParagraphStyle, styleRefOfListKind, } from './houseStyle';
import { captureEmphasis, restoreEmphasis } from './inlineEmphasis';
// Estilos de onde se entra numa lista SEM trocar o estilo: Normal vira
// "Parágrafo da Lista" sozinho (regra 3), e quem já é "Parágrafo da Lista"
// continua. Trocar o estilo apaga a ênfase do texto (ver inlineEmphasis.ts).
const ENTRA_SEM_TROCAR_ESTILO = new Set(['Normal', 'ListParagraph']);
const FORA_DE_LISTA = {
    inList: false,
    listId: null,
    level: 0,
    kind: null,
    rootKind: null,
    marker: '',
};
/** Estado de lista de cada parágrafo — dois syncs, qualquer que seja o número. */
export async function readListStates(context, paras) {
    if (!paras.length)
        return [];
    paras.forEach((p) => p.load('isListItem'));
    await context.sync();
    // `list`/`listItem` lançam ItemNotFound fora de lista: só para quem é item.
    const itens = paras.map((p) => (p.isListItem ? p.listItem : null));
    const listas = paras.map((p) => (p.isListItem ? p.list : null));
    itens.forEach((li) => li === null || li === void 0 ? void 0 : li.load('level,listString'));
    listas.forEach((l) => l === null || l === void 0 ? void 0 : l.load('id,levelTypes'));
    if (itens.some(Boolean))
        await context.sync();
    return paras.map((_, i) => {
        var _a;
        const li = itens[i];
        const l = listas[i];
        if (!li || !l)
            return FORA_DE_LISTA;
        const tipos = (_a = l.levelTypes) !== null && _a !== void 0 ? _a : [];
        return {
            inList: true,
            listId: l.id,
            level: li.level,
            kind: listKindOfLevelType(tipos[li.level]),
            rootKind: listKindOfLevelType(tipos[0]),
            marker: li.listString || '',
        };
    });
}
/** Marcador e recuo da marca em cada nível de uma lista que acabou de nascer. */
function defineHouseLevels(list, kind) {
    const def = HOUSE_LISTS[kind];
    def.levels.forEach((nivel, i) => {
        if (kind === 'bullet' && def.glyph) {
            list.setLevelBullet(i, Word.ListBullet.custom, def.glyph.charCodeAt(0), def.glyphFont);
        }
        else if (nivel.numberFormat) {
            list.setLevelNumbering(i, Word.ListNumbering.arabic, nivel.numberFormat);
        }
        list.setLevelIndents(i, nivel.textIndent, nivel.firstLineIndent);
    });
}
/**
 * Faz de `paras` (CONSECUTIVOS, na ordem do documento) itens de UMA lista da
 * marca, cada um no seu nível.
 *
 * Qual lista:
 *  - a que os próprios parágrafos já formam, se for do MESMO tipo — mudar o
 *    nível de dois itens no meio de uma lista não a parte em três. Ela passa a
 *    ser da marca: o marcador e o recuo de cada nível são redefinidos (a lista
 *    de bolinhas de um documento de terceiro vira "/"). Só fica nela quem já
 *    está em "Parágrafo da Lista"; item em outro estilo de lista ("Com
 *    marcadores" etc.) sai e volta, porque trocar o estilo o tiraria dela;
 *  - com `joinAdjacent`, também a lista do mesmo tipo logo acima (ou abaixo):
 *    inserir dois passos depois do passo 3 continua em "4." e "5.", em vez de
 *    uma lista nova que recomeçaria do "1.". É para INSERÇÃO; num lote de
 *    formatação, juntar com a vizinha fundiria duas listas distintas;
 *  - senão, uma lista nova com os níveis da marca.
 */
export async function makeHouseList(context, paras, kind, levels, opts = {}) {
    if (!paras.length)
        return { joined: null, markers: [], levels: [] };
    paras.forEach((p) => p.load('styleBuiltIn'));
    const estados = await readListStates(context, paras);
    // Estilo certo para ficar na lista em que está (regra 1: trocar o estilo de
    // um item o tiraria dela).
    const emParagrafoDaLista = paras.map((p) => String(p.styleBuiltIn) === 'ListParagraph');
    let alvo = null;
    const propria = estados.findIndex((e, i) => e.inList && e.rootKind === kind && e.listId !== null && emParagrafoDaLista[i]);
    if (propria >= 0) {
        // Nível do item de cima DENTRO da própria lista, para o 1º do grupo poder
        // ser subitem dele.
        const acima = paras[0].getPreviousOrNullObject();
        acima.load('isNullObject');
        await context.sync();
        const [deCima] = acima.isNullObject ? [FORA_DE_LISTA] : await readListStates(context, [acima]);
        const id = estados[propria].listId;
        alvo = { id, level: deCima.listId === id ? deCima.level : -1, lado: 'own' };
    }
    else if (opts.joinAdjacent) {
        const vizinhos = [
            [paras[0].getPreviousOrNullObject(), 'previous'],
            [paras[paras.length - 1].getNextOrNullObject(), 'next'],
        ];
        vizinhos.forEach(([v]) => v.load('isNullObject'));
        await context.sync();
        const reais = vizinhos.filter(([v]) => !v.isNullObject);
        const deles = await readListStates(context, reais.map(([v]) => v));
        const k = deles.findIndex((e) => e.inList && e.rootKind === kind && e.listId !== null);
        if (k >= 0)
            alvo = { id: deles[k].listId, level: deles[k].level, lado: reais[k][1] };
    }
    // Continuando a lista de cima, o 1º item pode ser subitem do último dela.
    const niveis = normalizeLevels(levels, (alvo === null || alvo === void 0 ? void 0 : alvo.lado) === 'previous' ? alvo.level : (alvo === null || alvo === void 0 ? void 0 : alvo.lado) === 'own' ? alvo.level : -1);
    const ficam = estados.map((e, i) => alvo !== null && e.inList && e.listId === alvo.id && emParagrafoDaLista[i]);
    // (1) Sai de qualquer OUTRA lista, com sync próprio: startNewList/attachToList
    // em quem ainda é item falha (regra 2). O detach preserva a ênfase do texto.
    const saem = paras.filter((_, i) => estados[i].inList && !ficam[i]);
    if (saem.length) {
        saem.forEach((p) => p.detachFromList());
        await context.sync();
    }
    // (2) Quem entra precisa estar em Normal ou já em "Parágrafo da Lista" —
    // entrando na lista, Normal vira "Parágrafo da Lista" sozinho (regra 3), sem
    // a numeração que o estilo traz no "Outro". Título ou citação passa por
    // Normal, e aí a ênfase do texto é guardada antes e devolvida depois.
    const entram = paras.filter((_, i) => !ficam[i]);
    entram.forEach((p) => p.load('styleBuiltIn'));
    await context.sync();
    const trocar = entram.filter((p) => !ENTRA_SEM_TROCAR_ESTILO.has(String(p.styleBuiltIn)));
    const retratos = await captureEmphasis(context, trocar);
    trocar.forEach((p) => {
        p.styleBuiltIn = Word.BuiltInStyleName.normal;
    });
    if (alvo) {
        const id = alvo.id;
        paras.forEach((p, i) => {
            if (!ficam[i])
                p.attachToList(id, niveis[i]);
        });
        // A lista do próprio documento vira lista da marca, nível a nível.
        const quemFica = paras.findIndex((_, i) => ficam[i]);
        if (alvo.lado === 'own' && quemFica >= 0)
            defineHouseLevels(paras[quemFica].list, kind);
    }
    else {
        const lista = paras[0].startNewList();
        lista.load('id');
        await context.sync();
        defineHouseLevels(lista, kind);
        paras.slice(1).forEach((p, k) => p.attachToList(lista.id, niveis[k + 1]));
    }
    await context.sync();
    // (3) O nível de todos (o 1º de uma lista nova nasce no 1º nível; quem já
    // estava na lista pode ter mudado de nível).
    paras.forEach((p, i) => {
        p.listItem.level = niveis[i];
    });
    // (4) Fonte, alinhamento e respiro da marca — sem trocar o estilo (regra 1).
    // A ênfase guardada volta depois: o título que virou item não fica em
    // negrito, porque o negrito dele era do estilo (captureEmphasis só guarda o
    // que é do texto).
    const ref = styleRefOfListKind(kind);
    paras.forEach((p) => applyHouseParagraphStyle(p, ref));
    retratos.forEach((r) => restoreEmphasis(r));
    await context.sync();
    const finais = await readListStates(context, paras);
    const entrou = ficam.some((f) => !f);
    return {
        joined: alvo && alvo.lado !== 'own' && entrou ? alvo.lado : null,
        markers: finais.map((e) => e.marker),
        levels: niveis,
    };
}
/**
 * Rede para estilos que NÃO são de lista (corpo, citação): trocar o estilo já
 * tira o parágrafo da lista (regra 4), mas se algum ficou preso — numeração
 * vinda do próprio estilo, por exemplo — sai por detach, com o recuo residual
 * zerado e o estilo reaplicado. Título fica fora de propósito: em documento de
 * terceiro, a numeração de título pode ser do estilo, e tirá-la apagaria o
 * número do tópico.
 */
export async function ensureOutOfList(context, paras, refs) {
    const estados = await readListStates(context, paras);
    const presos = paras.map((p, i) => ({ p, ref: refs[i] })).filter((_, i) => estados[i].inList);
    if (!presos.length)
        return 0;
    presos.forEach(({ p }) => p.detachFromList());
    await context.sync();
    // O detach devolve Normal: o estilo volta, e com ele a ênfase guardada.
    const retratos = await captureEmphasis(context, presos.map(({ p }) => p));
    presos.forEach(({ p, ref }, k) => {
        p.leftIndent = 0;
        p.firstLineIndent = 0;
        applyHouseParagraphStyle(p, ref);
        restoreEmphasis(retratos[k], { skipItalic: ref.startsWith('quote_') });
    });
    await context.sync();
    return presos.length;
}
/**
 * Tira do começo de cada parágrafo o marcador DIGITADO ("/\t", "- ", "1. ") —
 * quem desenha o marcador passa a ser a lista. Apaga só o trecho do marcador,
 * achado por busca no próprio parágrafo e conferido contra o INÍCIO dele:
 * reescrever o parágrafo inteiro perderia o negrito de um termo no começo do
 * item. Devolve quantos marcadores saíram.
 */
export async function removeTypedMarkers(context, paras) {
    if (!paras.length)
        return 0;
    paras.forEach((p) => p.load('text'));
    await context.sync();
    const alvos = paras
        .map((p) => ({ p, m: typedListMarker(p.text || '') }))
        .filter((a) => !!a.m);
    if (!alvos.length)
        return 0;
    const buscas = alvos.map(({ p, m }) => {
        const r = p.search(wordSearchText(m.marker), { matchCase: true });
        r.load('items');
        return r;
    });
    await context.sync();
    const primeiros = buscas.map((b) => { var _a; return (_a = b.items[0]) !== null && _a !== void 0 ? _a : null; });
    const posicoes = primeiros.map((r, k) => r ? r.getRange('Start').compareLocationWith(alvos[k].p.getRange('Start')) : null);
    await context.sync();
    let removidos = 0;
    primeiros.forEach((r, k) => {
        if (r && posicoes[k] && String(posicoes[k].value) === 'Equal') {
            r.delete();
            removidos++;
        }
    });
    if (removidos)
        await context.sync();
    return removidos;
}
/** "numerada" / "com bullets "/"" — para os retornos das ferramentas. */
export function listKindLabel(kind) {
    return kind === 'numbered' ? 'numerada' : 'com bullets "/"';
}
/** Resumo dos marcadores finais, ex.: `1. | 1.1. | 2.` (encurtado em listas longas). */
export function markersSummary(markers, max = 12) {
    const vis = markers.map((m) => m || '•');
    return vis.length <= max ? vis.join(' | ') : `${vis.slice(0, max).join(' | ')} | …`;
}
